import { useState } from 'react';
import { X, Ruler } from 'lucide-react';

// Standard size chart data for men's clothing
const sizeChartData = {
  shirts: [
    { size: 'S', chest: '36-38', length: '27', shoulder: '16.5' },
    { size: 'M', chest: '38-40', length: '28', shoulder: '17' },
    { size: 'L', chest: '40-42', length: '29', shoulder: '17.5' },
    { size: 'XL', chest: '42-44', length: '30', shoulder: '18' },
    { size: '2XL', chest: '44-46', length: '31', shoulder: '18.5' },
  ],
  tshirts: [
    { size: 'S', chest: '36-38', length: '26', shoulder: '16' },
    { size: 'M', chest: '38-40', length: '27', shoulder: '17' },
    { size: 'L', chest: '40-42', length: '28', shoulder: '18' },
    { size: 'XL', chest: '42-44', length: '29', shoulder: '19' },
    { size: '2XL', chest: '44-46', length: '30', shoulder: '20' },
  ],
  jeans: [
    { size: '28', waist: '28', hip: '36', length: '40' },
    { size: '30', waist: '30', hip: '38', length: '40' },
    { size: '32', waist: '32', hip: '40', length: '41' },
    { size: '34', waist: '34', hip: '42', length: '41' },
    { size: '36', waist: '36', hip: '44', length: '42' },
    { size: '38', waist: '38', hip: '46', length: '42' },
  ]
};

export const SizeChartModal = ({ isOpen, onClose, category = 'shirts', customImage = null }) => {
  const [activeTab, setActiveTab] = useState('chart');
  
  if (!isOpen) return null;

  // Determine which chart to show
  const chartType = category?.toLowerCase().includes('jean') || category?.toLowerCase().includes('trouser') 
    ? 'jeans' 
    : category?.toLowerCase().includes('tshirt') || category?.toLowerCase().includes('t-shirt')
    ? 'tshirts'
    : 'shirts';
  
  const chartData = sizeChartData[chartType];
  const isJeans = chartType === 'jeans';

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="size-chart-modal"
    >
      <div 
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-[#C59746]" />
            <h2 className="text-xl font-bold text-[#1A1A1A]">Size Guide</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-size-chart"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('chart')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === 'chart'
                ? 'text-[#C59746] border-b-2 border-[#C59746]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-size-chart"
          >
            Size Chart
          </button>
          <button
            onClick={() => setActiveTab('guide')}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === 'guide'
                ? 'text-[#C59746] border-b-2 border-[#C59746]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-measure-guide"
          >
            How to Measure
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'chart' && (
            <div>
              {customImage ? (
                <img 
                  src={customImage} 
                  alt="Size Chart" 
                  className="w-full rounded-lg"
                  data-testid="custom-size-chart-image"
                />
              ) : (
                <>
                  {/* Size Chart Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="size-chart-table">
                      <thead>
                        <tr className="bg-[#F5F5F3]">
                          <th className="p-3 text-left font-bold text-[#1A1A1A] border-b">Size</th>
                          {isJeans ? (
                            <>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Waist (in)</th>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Hip (in)</th>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Length (in)</th>
                            </>
                          ) : (
                            <>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Chest (in)</th>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Length (in)</th>
                              <th className="p-3 text-center font-bold text-[#1A1A1A] border-b">Shoulder (in)</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {chartData.map((row, index) => (
                          <tr key={row.size} className={index % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF8]'}>
                            <td className="p-3 font-bold text-[#C59746] border-b">{row.size}</td>
                            {isJeans ? (
                              <>
                                <td className="p-3 text-center text-gray-700 border-b">{row.waist}</td>
                                <td className="p-3 text-center text-gray-700 border-b">{row.hip}</td>
                                <td className="p-3 text-center text-gray-700 border-b">{row.length}</td>
                              </>
                            ) : (
                              <>
                                <td className="p-3 text-center text-gray-700 border-b">{row.chest}</td>
                                <td className="p-3 text-center text-gray-700 border-b">{row.length}</td>
                                <td className="p-3 text-center text-gray-700 border-b">{row.shoulder}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* International Size Conversion */}
                  <div className="mt-6">
                    <h3 className="font-bold text-[#1A1A1A] mb-3">International Size Conversion</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#F5F5F3]">
                            <th className="p-2 text-left font-bold text-[#1A1A1A] border-b">India/UK</th>
                            <th className="p-2 text-center font-bold text-[#1A1A1A] border-b">US</th>
                            <th className="p-2 text-center font-bold text-[#1A1A1A] border-b">EU</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="bg-white"><td className="p-2 border-b">S</td><td className="p-2 text-center border-b">S (4-6)</td><td className="p-2 text-center border-b">44-46</td></tr>
                          <tr className="bg-[#FAFAF8]"><td className="p-2 border-b">M</td><td className="p-2 text-center border-b">M (8-10)</td><td className="p-2 text-center border-b">48-50</td></tr>
                          <tr className="bg-white"><td className="p-2 border-b">L</td><td className="p-2 text-center border-b">L (12-14)</td><td className="p-2 text-center border-b">52-54</td></tr>
                          <tr className="bg-[#FAFAF8]"><td className="p-2 border-b">XL</td><td className="p-2 text-center border-b">XL (16-18)</td><td className="p-2 text-center border-b">56-58</td></tr>
                          <tr className="bg-white"><td className="p-2 border-b">2XL</td><td className="p-2 text-center border-b">XXL (20-22)</td><td className="p-2 text-center border-b">60-62</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'guide' && (
            <div className="space-y-6" data-testid="measurement-guide">
              {/* Measurement Guide SVG/Visual */}
              <div className="bg-[#F5F5F3] rounded-lg p-6 text-center">
                <svg viewBox="0 0 200 280" className="w-48 h-auto mx-auto">
                  {/* Body outline */}
                  <path 
                    d="M100 20 C115 20 125 35 125 50 L125 55 C145 60 160 75 160 95 L160 180 C160 190 155 195 150 195 L140 195 L140 270 L60 270 L60 195 L50 195 C45 195 40 190 40 180 L40 95 C40 75 55 60 75 55 L75 50 C75 35 85 20 100 20Z"
                    fill="none"
                    stroke="#C59746"
                    strokeWidth="2"
                  />
                  {/* Chest measurement line */}
                  <line x1="50" y1="100" x2="150" y2="100" stroke="#EF4444" strokeWidth="2" strokeDasharray="5,3"/>
                  <text x="100" y="115" textAnchor="middle" fill="#EF4444" fontSize="10" fontWeight="bold">CHEST</text>
                  
                  {/* Shoulder measurement line */}
                  <line x1="50" y1="65" x2="150" y2="65" stroke="#3B82F6" strokeWidth="2" strokeDasharray="5,3"/>
                  <text x="100" y="60" textAnchor="middle" fill="#3B82F6" fontSize="10" fontWeight="bold">SHOULDER</text>
                  
                  {/* Length measurement line */}
                  <line x1="170" y1="55" x2="170" y2="195" stroke="#10B981" strokeWidth="2" strokeDasharray="5,3"/>
                  <text x="180" y="125" textAnchor="start" fill="#10B981" fontSize="10" fontWeight="bold" transform="rotate(90 180 125)">LENGTH</text>
                </svg>
              </div>

              {/* Measurement Instructions */}
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1A1A1A]">Chest</h4>
                    <p className="text-sm text-gray-600">Measure around the fullest part of your chest, keeping the tape horizontal.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1A1A1A]">Shoulder</h4>
                    <p className="text-sm text-gray-600">Measure from the edge of one shoulder to the other, across the back.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1A1A1A]">Length</h4>
                    <p className="text-sm text-gray-600">Measure from the highest point of the shoulder down to the desired length.</p>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-[#C59746]/10 rounded-lg p-4">
                <h4 className="font-bold text-[#C59746] mb-2">Pro Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use a flexible measuring tape</li>
                  <li>• Keep the tape snug but not tight</li>
                  <li>• Measure over light clothing or underwear</li>
                  <li>• When in doubt, size up for comfort</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SizeChartModal;
